/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { cn } from '@/shared/ui/utils/tailwind';

export type ListProps = {
  children: React.ReactNode;
  type?: 'ordered' | 'unordered';
  spacing?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  center?: boolean;
};

export type ListItemProps = React.LiHTMLAttributes<HTMLLIElement> & {
  children: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
};

const spacingMap = {
  xs: 'gap-1',
  sm: 'gap-2',
  md: 'gap-3',
  lg: 'gap-4',
  xl: 'gap-6',
};

const sizeMap = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
};

/**
 * List component with Tailwind styling.
 * Replaces Mantine List.
 *
 * @param {ListProps} props - Component properties.
 * @returns {JSX.Element} Rendered list.
 */
type ListComponent = React.FC<ListProps> & { Item: React.FC<ListItemProps> };

export const List: ListComponent = (({
  children,
  type = 'unordered',
  spacing = 'md',
  size = 'md',
  className,
  center = false,
}) => {
  const Component = type === 'ordered' ? 'ol' : 'ul';
  return (
    <Component
      className={cn(
        'list-none',
        spacingMap[spacing],
        sizeMap[size],
        center && 'text-center',
        className
      )}
    >
      {children}
    </Component>
  );
}) as ListComponent;

/**
 * ListItem component
 */
export const ListItem: React.FC<ListItemProps> = ({
  children,
  icon,
  className,
  ...rest
}) => {
  return (
    <li className={cn('flex items-start gap-2', className)} {...rest}>
      {icon && <span className="flex-shrink-0 mt-0.5">{icon}</span>}
      <span className="flex-1">{children}</span>
    </li>
  );
};

// Attach ListItem to List for Mantine compatibility
List.Item = ListItem;

export default List;
